import { canTransition } from './state/transition.js';
/**
 * The cash drawer. §6.7 `POS-004`; Appendix C.7.
 *
 * > Cash session (drawer) management: open with float, record pay-ins/pay-outs with reason, **blind
 * > count at close**, variance report, session-level Z-report.
 *
 * **Blind is the requirement, and it is the only part of this that is hard.** A count taken with the
 * expected figure on screen is not a count, it is a confirmation: the person counting stops at the
 * number they were shown, and a short drawer stays short until somebody eventually notices a pattern
 * months later. §12's fraud table lists "till skimming" against exactly this control.
 *
 * So `expectedMinor` is never returned before a count is submitted, and the type makes that
 * structural rather than a rule somebody remembers: `BlindSession` has no expected figure to leak,
 * and `reconcile` is the only function that produces one, from a count it is given.
 */
export class CashSessionError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'CashSessionError';
    }
}
/**
 * Appendix C.7.
 *
 * `approved` sits between `variance_pending` and `closed` because a variance somebody signed off is
 * a different record from one that was never out: `ROLE-006` needs to show who approved it, and a
 * session that went straight to `closed` would have nowhere to say so.
 */
export const CASH_SESSION_STATUSES = [
    'open',
    'counting',
    'variance_pending',
    'approved',
    'closed',
];
export const CASH_SESSION_TRANSITIONS = {
    open: ['counting'],
    // A count that matches goes straight to closed; one that does not needs approving first.
    counting: ['variance_pending', 'closed'],
    variance_pending: ['approved'],
    approved: ['closed'],
    closed: [],
};
/**
 * What should be in the drawer.
 *
 * The float plus what came through the till plus every recorded pay-in and pay-out. A pay-out is a
 * negative movement, so this is one sum rather than two lists that could disagree about a sign.
 */
export function expectedInDrawer(session) {
    return (session.openingFloatMinor +
        session.cashTakenMinor +
        session.movements.reduce((sum, m) => sum + m.amountMinor, 0n));
}
/**
 * Reconcile a blind count against what the drawer should hold.
 *
 * Takes the count as an argument rather than reading it from the session, because the count has to
 * already exist for an expected figure to be computed at all. That ordering is the blind-count
 * control expressed as a function signature.
 */
export function reconcile(session, countedMinor) {
    if (session.status !== 'counting') {
        throw new CashSessionError('SESSION_NOT_COUNTING', `A session in ${session.status} is not being counted. Appendix C.7 runs open, counting, then closed.`);
    }
    if (countedMinor < 0n) {
        throw new CashSessionError('COUNT_NEGATIVE', 'A drawer cannot hold less than nothing. Record a pay-out with a reason instead.');
    }
    const expectedMinor = expectedInDrawer(session);
    const varianceMinor = countedMinor - expectedMinor;
    const balanced = varianceMinor === 0n;
    return {
        expectedMinor,
        countedMinor,
        varianceMinor,
        balanced,
        // A variance of any size needs approving. There is no tolerance threshold, because the spec
        // names none and `CLAUDE.md` forbids inventing one: a shop that wants to wave through small
        // differences sets a policy, and until they do every difference is somebody's to look at.
        nextStatus: balanced ? 'closed' : 'variance_pending',
    };
}
export function checkCashSessionTransition(from, to) {
    if (!canTransition(CASH_SESSION_TRANSITIONS, from, to)) {
        return {
            allowed: false,
            code: 'CASH_SESSION_TRANSITION_INVALID',
            message: `A cash session cannot go from ${from} to ${to} (Appendix C.7).`,
        };
    }
    return { allowed: true };
}
export function zReport(session, countedMinor) {
    const payIns = session.movements.filter((m) => m.amountMinor > 0n);
    const payOuts = session.movements.filter((m) => m.amountMinor < 0n);
    const expectedMinor = expectedInDrawer(session);
    return {
        sessionId: session.id,
        terminalId: session.terminalId,
        branchId: session.branchId,
        openingFloatMinor: session.openingFloatMinor,
        cashTakenMinor: session.cashTakenMinor,
        payInsMinor: payIns.reduce((sum, m) => sum + m.amountMinor, 0n),
        // Reported as a positive magnitude, because "paid out 3,000" is how a shop reads it and a
        // negative number in a column headed "paid out" is read twice by everybody, every time.
        payOutsMinor: -payOuts.reduce((sum, m) => sum + m.amountMinor, 0n),
        expectedMinor,
        countedMinor,
        varianceMinor: countedMinor - expectedMinor,
        movements: session.movements,
    };
}
/** `POS-004`: a movement without a reason is the one nobody can explain at the end of the day. */
export function validateMovement(movement) {
    if (movement.amountMinor === 0n) {
        throw new CashSessionError('MOVEMENT_ZERO', 'A movement of nothing records nothing.');
    }
    if (movement.reason.trim().length < 3) {
        throw new CashSessionError('MOVEMENT_REASON_REQUIRED', 'A pay-in or pay-out needs a reason (POS-004). Without one the variance report cannot explain itself.');
    }
}
//# sourceMappingURL=cash-session.js.map